import React, { useState, useEffect, useMemo, useCallback } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import {
  Search, Radar, Sparkles, Shield, Link2, Users, Globe, Gauge, Activity,
  TrendingUp, TrendingDown, Minus, AlertTriangle, AlertOctagon, Info,
  CheckCircle2, XCircle, RefreshCw, ExternalLink, ArrowUpRight, KeyRound,
  ShieldAlert, Target, BarChart3,
} from 'lucide-react'
import { getSession } from '../lib/session'
import AgentAsk from '../components/AgentAsk'

// SEO Intelligence — a Semrush-parity command surface for organic search, backlinks,
// competitor gaps, site health, and (our differentiator) AI-engine visibility. Six
// tabbed sections read six read-only endpoints under /api/v1/marketing-intel/seo/*.
// When the backend serves representative data it flags `sample:true`; we surface that
// honestly with a ribbon rather than passing sample figures off as live — the same
// contract TimeSeriesExplorer honours. Nothing here writes except "Run SEO scan",
// which POSTs /seo/scan and refetches. Every render is defensively guarded so a bad
// or missing field degrades to a dash, never a crash.
//
// VISUAL LAYER: the flat "Nunito / Intelligence Briefings" aesthetic — soft palette,
// rounded cards, pills, light + dark theme tokens — scoped under `.seo-root` so it
// never leaks. No chart libraries: rings and bars are inline SVG / divs (CSP-safe).
const API = '/api/v1/marketing-intel'

// ---- small, defensive helpers ---------------------------------------------
const isNum = (v) => typeof v === 'number' && !Number.isNaN(v)
const num = (v, fb = null) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : fb
}
const titleCase = (s) => String(s || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
const dash = (v) => (v == null || v === '' ? '—' : v)
// Compact number (1.2k / 3.4M). Returns '—' for non-numbers.
function fmtCompact(v, money = false) {
  const n = num(v)
  if (n == null) return '—'
  const a = Math.abs(n)
  let s
  if (a >= 1e9) s = (n / 1e9).toFixed(1) + 'B'
  else if (a >= 1e6) s = (n / 1e6).toFixed(1) + 'M'
  else if (a >= 1e3) s = (n / 1e3).toFixed(1) + 'k'
  else s = String(Math.round(n * 100) / 100)
  s = s.replace(/\.0([BMk])$/, '$1')
  return money ? '$' + s : s
}
const fmtInt = (v) => {
  const n = num(v)
  return n == null ? '—' : Math.round(n).toLocaleString()
}
const fmtPct = (v, digits = 0) => {
  const n = num(v)
  return n == null ? '—' : `${n.toFixed(digits)}%`
}
const fmtSignedPct = (v, digits = 1) => {
  const n = num(v)
  if (n == null) return '—'
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(digits)}%`
}
// Read the first present key from an object (endpoints vary in casing).
const pick = (obj, keys, fb = undefined) => {
  if (!obj || typeof obj !== 'object') return fb
  for (const k of keys) if (obj[k] != null) return obj[k]
  return fb
}
const arr = (v) => (Array.isArray(v) ? v : [])

// ---- scoped stylesheet (Intelligence-Briefings tokens, prefixed .seo-*) ----
const SEO_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800;900&display=swap');
.seo-root{
  --seo-bg:#FBFBF7; --seo-panel:#FFFFFF; --seo-panel2:#F6F6F0; --seo-line:#ECEBE3;
  --seo-ink:#2E3A45; --seo-mut:#7C8794; --seo-dim:#AEB6BD;
  --seo-accent:#4E9AC4; --seo-up:#4FAE5A; --seo-down:#E24A3B; --seo-warn:#EAB43C; --seo-purple:#8E7CC3;
  --seo-shadow:0 1px 2px rgba(46,58,69,.05),0 10px 26px rgba(46,58,69,.07);
  --seo-track:rgba(124,135,148,.14);
  font-family:Nunito,system-ui,-apple-system,Segoe UI,sans-serif;
  color:var(--seo-ink); -webkit-font-smoothing:antialiased;
}
[data-theme="dark"] .seo-root{
  --seo-bg:#0E1116; --seo-panel:#171B22; --seo-panel2:#12161C; --seo-line:#262C36;
  --seo-ink:#E7ECF1; --seo-mut:#95A0AD; --seo-dim:#5C6672;
  --seo-accent:#5FB0DB; --seo-up:#5CC47C; --seo-down:#F0575C; --seo-warn:#EAB43C; --seo-purple:#A796D6;
  --seo-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --seo-track:rgba(149,160,173,.16);
}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]) .seo-root{
  --seo-bg:#0E1116; --seo-panel:#171B22; --seo-panel2:#12161C; --seo-line:#262C36;
  --seo-ink:#E7ECF1; --seo-mut:#95A0AD; --seo-dim:#5C6672;
  --seo-accent:#5FB0DB; --seo-up:#5CC47C; --seo-down:#F0575C; --seo-warn:#EAB43C; --seo-purple:#A796D6;
  --seo-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --seo-track:rgba(149,160,173,.16);
}}
.seo-root *{box-sizing:border-box;}
.seo-title{font-size:24px;font-weight:900;letter-spacing:-.02em;margin:0;display:flex;align-items:center;gap:10px;color:var(--seo-ink);}
.seo-sub{margin:4px 0 0;color:var(--seo-mut);font-size:14px;font-weight:500;line-height:1.5;}
.seo-card{background:var(--seo-panel);border:1px solid var(--seo-line);border-radius:20px;box-shadow:var(--seo-shadow);padding:20px 22px;}
.seo-card.tight{padding:16px 18px;}
.seo-soft{background:var(--seo-panel2);border:1px solid var(--seo-line);border-radius:14px;}
.seo-sec-h{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:var(--seo-mut);display:flex;align-items:center;gap:8px;margin:0;}
.seo-elab{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:var(--seo-mut);margin:0;display:flex;align-items:center;gap:6px;}
.seo-narr{font-size:15px;font-weight:700;line-height:1.5;color:var(--seo-ink);margin:0;}
.seo-body{font-size:14px;font-weight:600;line-height:1.55;color:var(--seo-ink);margin:0;}
.seo-muted{color:var(--seo-mut);font-weight:600;}
.seo-dim{color:var(--seo-dim);font-weight:600;}
.seo-tabnum{font-variant-numeric:tabular-nums;}
/* ribbon (honest sample flag) */
.seo-ribbon{display:inline-flex;align-items:center;gap:7px;padding:6px 12px;border-radius:20px;
  background:rgba(234,180,60,.14);border:1px solid rgba(234,180,60,.35);color:#B4841B;font:800 11.5px Nunito;}
[data-theme="dark"] .seo-ribbon{color:#EAB43C;}
.seo-ribbon .seo-dot{width:7px;height:7px;border-radius:50%;background:#EAB43C;flex:none;}
/* pills */
.seo-pill{font-size:11px;font-weight:800;padding:4px 11px;border-radius:20px;display:inline-flex;align-items:center;gap:5px;}
.seo-chip{font-size:11px;font-weight:700;padding:4px 10px;border-radius:20px;background:var(--seo-panel2);border:1px solid var(--seo-line);color:var(--seo-mut);display:inline-flex;align-items:center;gap:5px;white-space:nowrap;}
.seo-badge{font-size:10px;font-weight:900;text-transform:uppercase;letter-spacing:.04em;padding:3px 8px;border-radius:7px;display:inline-flex;align-items:center;gap:4px;white-space:nowrap;}
/* tabs */
.seo-tabs{display:flex;gap:6px;overflow-x:auto;padding-bottom:4px;}
.seo-tab{flex:none;display:inline-flex;align-items:center;gap:7px;background:var(--seo-panel);border:1px solid var(--seo-line);border-radius:12px;
  padding:9px 14px;font:800 13px Nunito;color:var(--seo-mut);cursor:pointer;transition:.16s;box-shadow:var(--seo-shadow);white-space:nowrap;}
.seo-tab:hover{color:var(--seo-ink);transform:translateY(-1px);}
.seo-tab.active{background:var(--seo-accent);color:#fff;border-color:transparent;}
.seo-tab .seo-tab-x{font-size:10px;font-weight:900;padding:1px 6px;border-radius:20px;background:var(--seo-track);color:var(--seo-mut);}
.seo-tab.active .seo-tab-x{background:rgba(255,255,255,.24);color:#fff;}
/* KPI row */
.seo-kpis{display:grid;grid-template-columns:repeat(auto-fill,minmax(168px,1fr));gap:14px;}
.seo-kpi{background:var(--seo-panel);border:1px solid var(--seo-line);border-radius:16px;padding:15px 16px;box-shadow:var(--seo-shadow);}
.seo-kpi .seo-kl{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;color:var(--seo-dim);display:flex;align-items:center;gap:6px;}
.seo-kpi .seo-kv{font-size:26px;font-weight:900;color:var(--seo-ink);margin-top:8px;line-height:1;font-variant-numeric:tabular-nums;}
.seo-kpi .seo-kd{font-size:12px;font-weight:800;margin-top:7px;display:inline-flex;align-items:center;gap:4px;}
/* buttons */
.seo-btn{font-family:Nunito,system-ui,sans-serif;font-size:13px;font-weight:800;border-radius:12px;padding:10px 16px;cursor:pointer;display:inline-flex;align-items:center;gap:7px;border:1px solid var(--seo-line);background:var(--seo-panel);color:var(--seo-ink);transition:.16s;box-shadow:var(--seo-shadow);white-space:nowrap;}
.seo-btn:hover{transform:translateY(-1px);}
.seo-btn:disabled{opacity:.6;cursor:default;transform:none;}
.seo-btn.primary{background:var(--seo-accent);color:#fff;border-color:transparent;}
/* table */
.seo-tablewrap{overflow-x:auto;border:1px solid var(--seo-line);border-radius:16px;background:var(--seo-panel);box-shadow:var(--seo-shadow);}
table.seo-table{width:100%;border-collapse:collapse;min-width:640px;}
.seo-table thead th{font-size:10.5px;font-weight:900;text-transform:uppercase;letter-spacing:.05em;color:var(--seo-dim);text-align:left;padding:12px 14px;border-bottom:1px solid var(--seo-line);white-space:nowrap;background:var(--seo-panel2);}
.seo-table tbody td{font-size:13px;font-weight:600;color:var(--seo-ink);padding:11px 14px;border-bottom:1px solid var(--seo-line);vertical-align:middle;}
.seo-table tbody tr:last-child td{border-bottom:none;}
.seo-table tbody tr:hover td{background:var(--seo-panel2);}
.seo-table td.num,.seo-table th.num{text-align:right;font-variant-numeric:tabular-nums;}
.seo-table tr.toxic td{background:rgba(226,74,59,.06);}
.seo-table tr.toxic:hover td{background:rgba(226,74,59,.10);}
.seo-link{color:var(--seo-accent);font-weight:700;text-decoration:none;display:inline-flex;align-items:center;gap:4px;max-width:280px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.seo-link:hover{text-decoration:underline;}
/* score bars */
.seo-bar-track{flex:1;height:10px;background:var(--seo-track);border-radius:6px;overflow:hidden;}
.seo-bar-fill{height:100%;border-radius:6px;transition:width .6s cubic-bezier(.3,1,.4,1);}
/* severity stripe rows */
.seo-issue{display:flex;gap:12px;align-items:flex-start;padding:13px 15px;border-radius:12px;background:var(--seo-panel);border:1px solid var(--seo-line);box-shadow:var(--seo-shadow);border-left-width:4px;}
/* AI visibility hero */
.seo-hero{position:relative;overflow:hidden;background:linear-gradient(155deg, rgba(142,124,195,.10), rgba(78,154,196,.05) 55%, transparent);}
.seo-engine{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:11px 14px;border-radius:12px;background:var(--seo-panel2);border:1px solid var(--seo-line);}
.seo-spin{animation:seo-spin 1s linear infinite;}
@keyframes seo-spin{to{transform:rotate(360deg);}}
.seo-fade{animation:seo-fade .35s ease both;}
@keyframes seo-fade{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}
`

let _seoStyleInjected = false
function SeoStyle() {
  useEffect(() => {
    if (_seoStyleInjected) return
    const el = document.createElement('style')
    el.setAttribute('data-seo-style', '1')
    el.textContent = SEO_CSS
    document.head.appendChild(el)
    _seoStyleInjected = true
  }, [])
  return null
}

// tone tokens (rgba tints read well in both themes)
const TONE = {
  up:   { c: 'var(--seo-up)',     bg: 'rgba(79,174,90,.15)' },
  down: { c: 'var(--seo-down)',   bg: 'rgba(226,74,59,.14)' },
  warn: { c: 'var(--seo-warn)',   bg: 'rgba(234,180,60,.16)' },
  acc:  { c: 'var(--seo-accent)', bg: 'rgba(78,154,196,.14)' },
  pur:  { c: 'var(--seo-purple)', bg: 'rgba(142,124,195,.16)' },
  mut:  { c: 'var(--seo-mut)',    bg: 'rgba(124,135,148,.14)' },
}
const pill = (t) => ({ background: t.bg, color: t.c })
const softTint = (t) => ({ background: t.bg, borderColor: t.bg })

// severity → tone + icon
const SEV = {
  critical: { t: TONE.down, icon: AlertOctagon, label: 'Critical' },
  error:    { t: TONE.down, icon: AlertOctagon, label: 'Critical' },
  high:     { t: TONE.down, icon: AlertOctagon, label: 'Critical' },
  warn:     { t: TONE.warn, icon: AlertTriangle, label: 'Warning' },
  warning:  { t: TONE.warn, icon: AlertTriangle, label: 'Warning' },
  medium:   { t: TONE.warn, icon: AlertTriangle, label: 'Warning' },
  notice:   { t: TONE.acc,  icon: Info, label: 'Notice' },
  info:     { t: TONE.acc,  icon: Info, label: 'Notice' },
  low:      { t: TONE.acc,  icon: Info, label: 'Notice' },
}
const sevOf = (s) => SEV[String(s || 'notice').toLowerCase()] || SEV.notice
const sevRank = (s) => {
  const k = String(s || '').toLowerCase()
  if (['critical', 'error', 'high'].includes(k)) return 0
  if (['warn', 'warning', 'medium'].includes(k)) return 1
  return 2
}

// health status → tone
const statusTone = (s) => {
  const k = String(s || '').toLowerCase()
  if (['good', 'ok', 'pass', 'healthy', 'green'].includes(k)) return TONE.up
  if (['warn', 'warning', 'medium', 'attention', 'yellow'].includes(k)) return TONE.warn
  if (['bad', 'critical', 'error', 'fail', 'poor', 'red'].includes(k)) return TONE.down
  return TONE.mut
}

// intent → tone
const intentTone = (s) => {
  const k = String(s || '').toLowerCase()
  if (k.startsWith('transact') || k === 'commercial') return TONE.up
  if (k.startsWith('commerc')) return TONE.warn
  if (k.startsWith('navig')) return TONE.pur
  return TONE.acc // informational / unknown
}

// A 0–100 score → tone bucket (site-audit / authority style grading).
const scoreTone = (v) => {
  const n = num(v, 0)
  if (n >= 70) return TONE.up
  if (n >= 40) return TONE.warn
  return TONE.down
}

// ---- SVG ring gauge (no chart libs) ---------------------------------------
function RingGauge({ value, max = 100, size = 132, stroke = 12, color, sub, label }) {
  const v = Math.max(0, Math.min(num(value, 0), max))
  const r = (size - stroke) / 2
  const circ = 2 * Math.PI * r
  const pct = max > 0 ? v / max : 0
  const dash = circ * pct
  const cx = size / 2
  return (
    <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--seo-track)" strokeWidth={stroke} />
        <circle
          cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`} transform={`rotate(-90 ${cx} ${cx})`}
          style={{ transition: 'stroke-dasharray .7s cubic-bezier(.3,1,.4,1)' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span className="seo-tabnum" style={{ fontSize: 30, fontWeight: 900, color: 'var(--seo-ink)', lineHeight: 1 }}>
          {isNum(num(value)) ? Math.round(num(value)) : '—'}
        </span>
        {sub != null && <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--seo-mut)', marginTop: 2 }}>{sub}</span>}
        {label != null && <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--seo-dim)', marginTop: 3 }}>{label}</span>}
      </div>
    </div>
  )
}

// ---- position delta arrow --------------------------------------------------
function PositionDelta({ position, prev }) {
  const p = num(position)
  const pv = num(prev)
  if (p == null) return <span className="seo-dim">—</span>
  if (pv == null) return <span className="seo-tabnum" style={{ fontWeight: 800 }}>{p}</span>
  // A lower position number is better, so prev - current > 0 means we climbed.
  const delta = pv - p
  const t = delta > 0 ? TONE.up : delta < 0 ? TONE.down : TONE.mut
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span className="seo-tabnum" style={{ fontWeight: 800 }}>{p}</span>
      <span className="seo-tabnum" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: t.c, fontWeight: 800, fontSize: 12 }}>
        <Icon size={12} /> {delta === 0 ? '0' : Math.abs(delta)}
      </span>
    </span>
  )
}

const TABS = [
  { id: 'overview',    label: 'Overview',      icon: Gauge },
  { id: 'keywords',    label: 'Keywords',      icon: KeyRound },
  { id: 'backlinks',   label: 'Backlinks',     icon: Link2 },
  { id: 'competitors', label: 'Competitors',   icon: Users },
  { id: 'audit',       label: 'Site Audit',    icon: Shield },
  { id: 'ai',          label: 'AI Visibility', icon: Sparkles, hero: true },
]

export default function SeoIntelligence() {
  const session = getSession()
  const [tab, setTab] = useState('overview')
  const [scanning, setScanning] = useState(false)
  // A bump counter that child sections watch to refetch after a scan.
  const [reloadKey, setReloadKey] = useState(0)

  const runScan = useCallback(async () => {
    setScanning(true)
    try {
      const r = await axios.post(`${API}/seo/scan`)
      const found = pick(r.data, ['issues', 'found', 'checked'])
      toast.success(
        isNum(num(found)) ? `SEO scan complete — ${Math.round(num(found))} signals refreshed` : 'SEO scan complete',
      )
      setReloadKey((k) => k + 1)
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not run the SEO scan')
    } finally {
      setScanning(false)
    }
  }, [])

  return (
    <div className="seo-root space-y-8">
      <SeoStyle />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="seo-title">
            <Radar size={24} style={{ color: 'var(--seo-accent)' }} /> SEO Intelligence
          </h2>
          <p className="seo-sub">
            Authority, keywords, backlinks, competitor gaps and site health — plus AI-engine visibility, the metric classic SEO tools miss.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="seo-pill" style={pill(TONE.pur)}>
            <Sparkles size={13} /> AI-visibility native
          </span>
          {session?.namespaceId && (
            <button onClick={runScan} disabled={scanning} className="seo-btn primary">
              {scanning ? <RefreshCw size={15} className="seo-spin" /> : <Radar size={15} />}
              {scanning ? 'Scanning…' : 'Run SEO scan'}
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: '42rem' }}><AgentAsk service="seo" agentName="SEO Analyst Agent" starters={['Which keywords am I closest to ranking for?', 'Is any AI engine citing us?']} /></div>

      {!session?.namespaceId && (
        <div className="seo-card" style={{ textAlign: 'center', padding: '48px 22px' }}>
          <Radar size={32} style={{ color: 'var(--seo-dim)', margin: '0 auto 12px' }} />
          <p className="seo-muted" style={{ margin: 0 }}>Connect a workspace to activate SEO Intelligence.</p>
        </div>
      )}

      {session?.namespaceId && (
        <>
          <div className="seo-tabs" role="tablist" aria-label="SEO sections">
            {TABS.map((t) => {
              const Icon = t.icon
              const active = t.id === tab
              return (
                <button key={t.id} role="tab" aria-selected={active}
                  className={`seo-tab ${active ? 'active' : ''}`} onClick={() => setTab(t.id)}>
                  <Icon size={15} /> {t.label}
                  {t.hero && <span className="seo-tab-x">NEW</span>}
                </button>
              )
            })}
          </div>

          {/* Sections remount on scan so each refetches its endpoint. */}
          {tab === 'overview' && <OverviewSection key={`ov-${reloadKey}`} onJump={setTab} />}
          {tab === 'keywords' && <KeywordsSection key={`kw-${reloadKey}`} />}
          {tab === 'backlinks' && <BacklinksSection key={`bl-${reloadKey}`} />}
          {tab === 'competitors' && <CompetitorsSection key={`cp-${reloadKey}`} />}
          {tab === 'audit' && <AuditSection key={`au-${reloadKey}`} />}
          {tab === 'ai' && <AiVisibilitySection key={`ai-${reloadKey}`} />}
        </>
      )}
    </div>
  )
}

// ---- shared fetch hook -----------------------------------------------------
// Loads one endpoint; exposes { data, sample, loading, error }. Never throws to render.
function useSeo(path) {
  const [state, setState] = useState({ data: null, sample: false, loading: true, error: false })
  useEffect(() => {
    let alive = true
    setState((s) => ({ ...s, loading: true, error: false }))
    axios.get(`${API}${path}`)
      .then((r) => {
        if (!alive) return
        const d = r?.data && typeof r.data === 'object' ? r.data : {}
        setState({ data: d, sample: !!d.sample, loading: false, error: false })
      })
      .catch(() => { if (alive) setState({ data: null, sample: false, loading: false, error: true }) })
    return () => { alive = false }
  }, [path])
  return state
}

function SampleRibbon({ show }) {
  if (!show) return null
  return (
    <span className="seo-ribbon">
      <span className="seo-dot" /> sample data — connect Google Search Console or Semrush to go live
    </span>
  )
}
function Loading({ label }) {
  return <div className="seo-card seo-muted" style={{ textAlign: 'center', padding: '32px 22px' }}>{label || 'Loading…'}</div>
}
function ErrorCard({ label }) {
  return (
    <div className="seo-card" style={{ textAlign: 'center', padding: '36px 22px' }}>
      <AlertTriangle size={26} style={{ color: 'var(--seo-warn)', margin: '0 auto 8px' }} />
      <p className="seo-muted" style={{ fontSize: 13.5, margin: 0 }}>{label || 'Could not load this section. Run a scan or try again.'}</p>
    </div>
  )
}

// ---- OVERVIEW --------------------------------------------------------------
function OverviewSection({ onJump }) {
  const { data, sample, loading, error } = useSeo('/seo/overview')
  if (loading) return <Loading label="Reading your search footprint…" />
  if (error || !data) return <ErrorCard />

  // The API contract is NESTED: { authority:{score,grade,delta}, organic:{traffic,keywords,...},
  // backlinks:{total,referringDomains,...}, aiVisibility:{score,engines[]} }. Read nested first,
  // fall back to flat aliases so either shape renders.
  const auth = (data.authority && typeof data.authority === 'object') ? data.authority : {}
  const org = (data.organic && typeof data.organic === 'object') ? data.organic : {}
  const bl = (data.backlinks && typeof data.backlinks === 'object') ? data.backlinks : {}
  const av = (data.aiVisibility && typeof data.aiVisibility === 'object') ? data.aiVisibility : {}

  const authority = num(pick(auth, ['score']) ?? pick(data, ['authorityScore', 'domainAuthority', 'ds']))
  const grade = pick(auth, ['grade']) ?? pick(data, ['authorityGrade', 'grade'])
  const traffic = pick(org, ['traffic']) ?? pick(data, ['organicTraffic', 'traffic', 'organic_traffic'])
  const trafficDelta = pick(org, ['trafficDelta']) ?? pick(data, ['organicTrafficDelta', 'trafficDelta', 'traffic_change', 'trafficChange'])
  const keywords = pick(org, ['keywords']) ?? pick(data, ['organicKeywords', 'organic_keywords', 'keywordCount'])
  const backlinks = pick(bl, ['total']) ?? pick(data, ['backlinkCount', 'totalBacklinks'])
  const refDomains = pick(bl, ['referringDomains']) ?? pick(data, ['referringDomains', 'refDomains', 'referring_domains'])
  const aiVis = num(pick(av, ['score']) ?? pick(data, ['aiVisibilityScore', 'ai_visibility'], 0), 0)
  const engines = arr(pick(av, ['engines']) ?? pick(data, ['engines', 'aiEngines', 'ai_engines']))
  const health = arr(pick(data, ['health', 'healthChecks', 'checks']))

  const dt = num(trafficDelta)
  const dTone = dt == null ? TONE.mut : dt > 0 ? TONE.up : dt < 0 ? TONE.down : TONE.mut
  const DIcon = dt == null ? Minus : dt > 0 ? TrendingUp : dt < 0 ? TrendingDown : Minus
  const aTone = scoreTone(authority == null ? 0 : (authority <= 10 ? authority * 10 : authority))

  return (
    <div className="space-y-6 seo-fade">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="seo-sec-h"><Gauge size={14} /> Domain overview</h3>
        <SampleRibbon show={sample} />
      </div>

      <div className="seo-kpis">
        <div className="seo-kpi">
          <div className="seo-kl"><Shield size={12} /> Authority</div>
          <div className="seo-kv">
            {authority == null ? '—' : authority}
            {grade != null && <span style={{ fontSize: 14, marginLeft: 8, color: aTone.c, fontWeight: 900 }}>{grade}</span>}
          </div>
          <div className="seo-kd" style={{ color: 'var(--seo-dim)' }}>Domain strength</div>
        </div>
        <div className="seo-kpi">
          <div className="seo-kl"><Activity size={12} /> Organic traffic</div>
          <div className="seo-kv">{fmtCompact(traffic)}</div>
          <div className="seo-kd" style={{ color: dTone.c }}><DIcon size={13} /> {fmtSignedPct(trafficDelta)}</div>
        </div>
        <div className="seo-kpi">
          <div className="seo-kl"><KeyRound size={12} /> Organic keywords</div>
          <div className="seo-kv">{fmtInt(keywords)}</div>
          <div className="seo-kd" style={{ color: 'var(--seo-dim)' }}>Ranking terms</div>
        </div>
        <div className="seo-kpi">
          <div className="seo-kl"><Link2 size={12} /> Backlinks</div>
          <div className="seo-kv">{fmtInt(backlinks)}</div>
          <div className="seo-kd" style={{ color: 'var(--seo-dim)' }}>Inbound links</div>
        </div>
        <div className="seo-kpi">
          <div className="seo-kl"><Globe size={12} /> Referring domains</div>
          <div className="seo-kv">{fmtInt(refDomains)}</div>
          <div className="seo-kd" style={{ color: 'var(--seo-dim)' }}>Unique sources</div>
        </div>
      </div>

      {/* AI VISIBILITY hero — the differentiator */}
      <div className="seo-card seo-hero">
        <div className="flex items-center justify-between gap-3 flex-wrap" style={{ marginBottom: 16 }}>
          <span className="seo-sec-h"><Sparkles size={16} style={{ color: 'var(--seo-purple)' }} /> AI visibility</span>
          <button className="seo-btn" onClick={() => onJump && onJump('ai')} style={{ padding: '7px 13px', fontSize: 12 }}>
            Full breakdown <ArrowUpRight size={13} />
          </button>
        </div>
        <div className="flex items-center gap-6 flex-wrap">
          <RingGauge value={aiVis} max={100} color="var(--seo-purple)" sub="/ 100" label="Cited score" />
          <div style={{ flex: 1, minWidth: 240 }}>
            <p className="seo-body" style={{ marginBottom: 12 }}>
              How often the leading answer engines cite your domain when users ask about your space.
              {num(aiVis, 0) === 0 && <span className="seo-muted"> You are not being cited yet — the whitespace is the opportunity.</span>}
            </p>
            <EngineChips engines={engines} />
          </div>
        </div>
      </div>

      {/* HEALTH list */}
      <div>
        <h3 className="seo-sec-h" style={{ marginBottom: 12 }}><Activity size={14} /> Health</h3>
        {health.length === 0 ? (
          <div className="seo-card" style={{ textAlign: 'center', padding: '30px 22px' }}>
            <CheckCircle2 size={24} style={{ color: 'var(--seo-up)', margin: '0 auto 8px', opacity: .8 }} />
            <p className="seo-muted" style={{ fontSize: 13.5, margin: 0 }}>No health signals yet. Run an SEO scan to populate them.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {health.map((h, i) => {
              const t = statusTone(pick(h, ['status', 'severity', 'state']))
              const label = pick(h, ['label', 'title', 'name', 'check'])
              const detail = pick(h, ['detail', 'description', 'message', 'note'])
              return (
                <div key={i} className="seo-card tight seo-fade" style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <span style={{ flex: 'none', width: 10, height: 10, borderRadius: '50%', background: t.c, marginTop: 5 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p className="seo-narr" style={{ fontSize: 14 }}>{dash(label)}</p>
                    {detail != null && typeof detail !== 'object' && <p className="seo-muted" style={{ fontSize: 12.5, marginTop: 3 }}>{String(detail)}</p>}
                  </div>
                  <span className="seo-pill" style={{ ...pill(t), flex: 'none', textTransform: 'capitalize' }}>
                    {dash(pick(h, ['status', 'severity', 'state']))}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// per-engine cited / not-cited chips (shared by Overview + AI tab)
function EngineChips({ engines }) {
  const list = arr(engines)
  if (list.length === 0) {
    return <p className="seo-dim" style={{ fontSize: 13, margin: 0 }}>No engine data yet — run a scan to probe ChatGPT, Google AI Overview, Gemini and Perplexity.</p>
  }
  return (
    <div className="flex flex-wrap gap-2">
      {list.map((e, i) => {
        const name = pick(e, ['name', 'engine', 'label']) || `Engine ${i + 1}`
        const cited = !!pick(e, ['cited', 'isCited', 'present'], false)
        const t = cited ? TONE.up : TONE.mut
        const Icon = cited ? CheckCircle2 : XCircle
        return (
          <span key={i} className="seo-badge" style={pill(t)}>
            <Icon size={12} /> {name}{cited ? '' : ' · not cited'}
          </span>
        )
      })}
    </div>
  )
}

// ---- KEYWORDS --------------------------------------------------------------
function KeywordsSection() {
  const { data, sample, loading, error } = useSeo('/seo/keywords')
  if (loading) return <Loading label="Loading ranked keywords…" />
  if (error || !data) return <ErrorCard />
  const rows = arr(pick(data, ['keywords', 'rows', 'items', 'data']))

  return (
    <div className="space-y-4 seo-fade">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="seo-sec-h"><KeyRound size={14} /> Ranked keywords <span className="seo-dim" style={{ fontWeight: 700 }}>· {rows.length}</span></h3>
        <SampleRibbon show={sample} />
      </div>

      {rows.length === 0 ? (
        <ErrorCard label="No ranking keywords tracked yet. Connect Search Console or Semrush, then run a scan." />
      ) : (
        <div className="seo-tablewrap">
          <table className="seo-table">
            <thead>
              <tr>
                <th>Keyword</th>
                <th className="num">Position</th>
                <th className="num">Volume</th>
                <th className="num">CPC</th>
                <th className="num">Est. traffic</th>
                <th>Intent</th>
                <th>URL</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const kw = pick(r, ['keyword', 'term', 'query', 'phrase'])
                const pos = pick(r, ['position', 'pos', 'rank'])
                const prev = pick(r, ['prevPosition', 'previousPosition', 'prev_position', 'previous_rank', 'prevPos'])
                const vol = pick(r, ['volume', 'searchVolume', 'search_volume', 'vol'])
                const cpc = pick(r, ['cpc', 'costPerClick'])
                const traffic = pick(r, ['traffic', 'estTraffic', 'est_traffic', 'trafficEstimate'])
                const intent = pick(r, ['intent', 'searchIntent'])
                const url = pick(r, ['url', 'page', 'landingPage', 'link'])
                const it = intentTone(intent)
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 800, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{dash(kw)}</td>
                    <td className="num"><PositionDelta position={pos} prev={prev} /></td>
                    <td className="num">{fmtInt(vol)}</td>
                    <td className="num">{cpc == null ? '—' : fmtCompact(cpc, true)}</td>
                    <td className="num">{fmtInt(traffic)}</td>
                    <td>{intent == null ? <span className="seo-dim">—</span> : <span className="seo-badge" style={pill(it)}>{titleCase(intent)}</span>}</td>
                    <td>
                      {url == null ? <span className="seo-dim">—</span> : (
                        <a className="seo-link" href={String(url)} target="_blank" rel="noopener noreferrer" title={String(url)}>
                          {String(url).replace(/^https?:\/\//, '')} <ExternalLink size={11} className="shrink-0" />
                        </a>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ---- BACKLINKS -------------------------------------------------------------
function BacklinksSection() {
  const { data, sample, loading, error } = useSeo('/seo/backlinks')
  if (loading) return <Loading label="Auditing your backlink profile…" />
  if (error || !data) return <ErrorCard />

  // Contract: { summary:{referringDomains,toxicPct,follow,nofollow}, rows:[...] }. Read
  // summary first, fall back to flat aliases / row-derived values.
  const sum = (data.summary && typeof data.summary === 'object') ? data.summary : {}
  const rows = arr(pick(data, ['backlinks', 'links', 'rows', 'items']))
  const refDomains = pick(sum, ['referringDomains']) ?? pick(data, ['referringDomains', 'refDomains', 'referring_domains'])
  let toxicPct = num(pick(sum, ['toxicPct']) ?? pick(data, ['toxicPct', 'toxicPercent', 'toxic_pct', 'toxicShare']))
  let followPct = num(pick(sum, ['followPct']) ?? pick(data, ['followPct', 'followPercent', 'follow_pct']))
  let nofollowPct = num(pick(sum, ['nofollowPct']) ?? pick(data, ['nofollowPct', 'nofollowPercent', 'nofollow_pct']))

  // Derive percentages from the rows when the summary omits them, so the chips are never blank.
  if (rows.length) {
    const isTox = (r) => !!pick(r, ['toxic', 'isToxic', 'spam'], false)
    const isFollow = (r) => String(pick(r, ['type', 'linkType', 'rel'], '')).toLowerCase().includes('nofollow') ? false : true
    if (toxicPct == null) toxicPct = Math.round((rows.filter(isTox).length / rows.length) * 100)
    if (followPct == null) followPct = Math.round((rows.filter(isFollow).length / rows.length) * 100)
    if (nofollowPct == null && followPct != null) nofollowPct = Math.max(0, 100 - followPct)
  }

  const toxTone = toxicPct == null ? TONE.mut : toxicPct >= 40 ? TONE.down : toxicPct >= 15 ? TONE.warn : TONE.up

  return (
    <div className="space-y-5 seo-fade">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="seo-sec-h"><Link2 size={14} /> Backlink profile</h3>
        <SampleRibbon show={sample} />
      </div>

      <div className="seo-kpis">
        <div className="seo-kpi">
          <div className="seo-kl"><Globe size={12} /> Referring domains</div>
          <div className="seo-kv">{fmtInt(refDomains)}</div>
          <div className="seo-kd" style={{ color: 'var(--seo-dim)' }}>Unique linking sites</div>
        </div>
        <div className="seo-kpi" style={{ borderColor: toxTone.c }}>
          <div className="seo-kl"><ShieldAlert size={12} style={{ color: toxTone.c }} /> Toxic</div>
          <div className="seo-kv" style={{ color: toxTone.c }}>{fmtPct(toxicPct)}</div>
          <div className="seo-kd" style={{ color: toxTone.c }}>
            {toxicPct == null ? '' : toxicPct >= 40 ? 'Disavow urgently' : toxicPct >= 15 ? 'Review recommended' : 'Healthy'}
          </div>
        </div>
        <div className="seo-kpi">
          <div className="seo-kl"><CheckCircle2 size={12} /> Follow</div>
          <div className="seo-kv">{fmtPct(followPct)}</div>
          <div className="seo-kd" style={{ color: 'var(--seo-dim)' }}>Passes authority</div>
        </div>
        <div className="seo-kpi">
          <div className="seo-kl"><Minus size={12} /> Nofollow</div>
          <div className="seo-kv">{fmtPct(nofollowPct)}</div>
          <div className="seo-kd" style={{ color: 'var(--seo-dim)' }}>No authority passed</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <ErrorCard label="No backlinks discovered yet. Connect a backlink source, then run a scan." />
      ) : (
        <div className="seo-tablewrap">
          <table className="seo-table">
            <thead>
              <tr>
                <th>Source domain</th>
                <th>Anchor</th>
                <th>Type</th>
                <th className="num">Authority</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const toxic = !!pick(r, ['toxic', 'isToxic', 'spam'], false)
                const domain = pick(r, ['sourceDomain', 'source', 'domain', 'fromDomain', 'referringDomain'])
                const anchor = pick(r, ['anchor', 'anchorText', 'anchor_text', 'text'])
                const type = pick(r, ['type', 'linkType', 'rel'])
                const authority = pick(r, ['authority', 'domainAuthority', 'ds', 'score'])
                return (
                  <tr key={i} className={toxic ? 'toxic' : ''}>
                    <td style={{ fontWeight: 800, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>{dash(domain)}</td>
                    <td className="seo-muted" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{dash(anchor)}</td>
                    <td><span className="seo-chip">{dash(type == null ? null : titleCase(type))}</span></td>
                    <td className="num" style={{ fontWeight: 800 }}>{authority == null ? '—' : authority}</td>
                    <td>
                      {toxic
                        ? <span className="seo-badge" style={pill(TONE.down)}><ShieldAlert size={11} /> TOXIC</span>
                        : <span className="seo-badge" style={pill(TONE.up)}><CheckCircle2 size={11} /> Clean</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ---- COMPETITORS -----------------------------------------------------------
function CompetitorsSection() {
  const { data, sample, loading, error } = useSeo('/seo/competitors')
  if (loading) return <Loading label="Mapping the competitive gap…" />
  if (error || !data) return <ErrorCard />
  const rows = arr(pick(data, ['competitors', 'rows', 'items', 'data']))

  return (
    <div className="space-y-4 seo-fade">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="seo-sec-h"><Users size={14} /> Competitor gap <span className="seo-dim" style={{ fontWeight: 700 }}>· {rows.length}</span></h3>
        <SampleRibbon show={sample} />
      </div>

      {rows.length === 0 ? (
        <ErrorCard label="No competitors mapped yet. Run a scan to surface who ranks for your terms." />
      ) : (
        <div className="seo-tablewrap">
          <table className="seo-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th className="num">Common keywords</th>
                <th className="num">Their keywords</th>
                <th className="num">Authority</th>
                <th>Overlap</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const domain = pick(r, ['domain', 'competitor', 'name', 'site'])
                const common = pick(r, ['commonKeywords', 'common', 'shared', 'common_keywords'])
                const seKw = pick(r, ['seKeywords', 'theirKeywords', 'keywords', 'se_keywords', 'totalKeywords'])
                const authority = pick(r, ['authority', 'domainAuthority', 'ds', 'score'])
                const overlap = num(pick(r, ['overlap', 'overlapPct', 'overlap_pct', 'overlapPercent']))
                const ot = scoreTone(overlap)
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 800, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>{dash(domain)}</td>
                    <td className="num">{fmtInt(common)}</td>
                    <td className="num">{fmtInt(seKw)}</td>
                    <td className="num" style={{ fontWeight: 800 }}>{authority == null ? '—' : authority}</td>
                    <td>
                      {overlap == null ? <span className="seo-dim">—</span> : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 140 }}>
                          <div className="seo-bar-track"><div className="seo-bar-fill" style={{ width: `${Math.max(2, Math.min(overlap, 100))}%`, background: ot.c }} /></div>
                          <span className="seo-tabnum" style={{ fontWeight: 800, fontSize: 12, color: ot.c, flex: 'none' }}>{fmtPct(overlap)}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ---- SITE AUDIT ------------------------------------------------------------
function AuditSection() {
  const { data, sample, loading, error } = useSeo('/seo/audit')
  if (loading) return <Loading label="Running the site audit…" />
  if (error || !data) return <ErrorCard />

  const score = num(pick(data, ['score', 'overallScore', 'healthScore', 'overall']))
  const categories = arr(pick(data, ['categories', 'scores', 'categoryScores']))
  const issues = arr(pick(data, ['issues', 'problems', 'findings']))
    .slice()
    .sort((a, b) => sevRank(pick(a, ['severity', 'level'])) - sevRank(pick(b, ['severity', 'level'])))
  const st = scoreTone(score)

  const counts = issues.reduce((acc, it) => {
    const r = sevRank(pick(it, ['severity', 'level']))
    const key = r === 0 ? 'critical' : r === 1 ? 'warn' : 'notice'
    acc[key] = (acc[key] || 0) + (num(pick(it, ['count']), 1) || 1)
    return acc
  }, {})

  return (
    <div className="space-y-6 seo-fade">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="seo-sec-h"><Shield size={14} /> Site audit</h3>
        <SampleRibbon show={sample} />
      </div>

      <div className="seo-card">
        <div className="flex items-center gap-6 flex-wrap">
          <RingGauge value={score} max={100} color={st.c} sub="/ 100" label="Site health" />
          <div style={{ flex: 1, minWidth: 240 }}>
            <p className="seo-narr" style={{ fontSize: 16, marginBottom: 4 }}>
              {score == null ? 'Not scored yet' : score >= 70 ? 'Healthy — a few things to polish' : score >= 40 ? 'Needs attention' : 'Poor — critical issues to fix'}
            </p>
            <p className="seo-sub" style={{ marginBottom: 14 }}>Overall crawl-and-audit score across on-page, technical and content checks.</p>
            <div className="flex flex-wrap gap-2">
              <span className="seo-badge" style={pill(TONE.down)}><AlertOctagon size={11} /> {num(counts.critical, 0)} critical</span>
              <span className="seo-badge" style={pill(TONE.warn)}><AlertTriangle size={11} /> {num(counts.warn, 0)} warnings</span>
              <span className="seo-badge" style={pill(TONE.acc)}><Info size={11} /> {num(counts.notice, 0)} notices</span>
            </div>
          </div>
        </div>
      </div>

      {categories.length > 0 && (
        <div className="seo-card">
          <p className="seo-elab" style={{ marginBottom: 14 }}><BarChart3 size={13} /> Category scores</p>
          <div className="space-y-3">
            {categories.map((c, i) => {
              const name = pick(c, ['name', 'label', 'category', 'key'])
              const v = num(pick(c, ['score', 'value', 'pct']))
              const ct = scoreTone(v)
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span className="seo-body" style={{ width: 150, flex: 'none', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dash(name == null ? null : titleCase(name))}</span>
                  <div className="seo-bar-track"><div className="seo-bar-fill" style={{ width: `${Math.max(2, Math.min(num(v, 0), 100))}%`, background: ct.c }} /></div>
                  <span className="seo-tabnum" style={{ width: 42, textAlign: 'right', flex: 'none', fontWeight: 800, fontSize: 13, color: ct.c }}>{v == null ? '—' : Math.round(v)}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div>
        <p className="seo-elab" style={{ marginBottom: 12 }}><AlertTriangle size={13} /> Issues by severity</p>
        {issues.length === 0 ? (
          <div className="seo-card" style={{ textAlign: 'center', padding: '30px 22px' }}>
            <CheckCircle2 size={24} style={{ color: 'var(--seo-up)', margin: '0 auto 8px', opacity: .8 }} />
            <p className="seo-muted" style={{ fontSize: 13.5, margin: 0 }}>No issues found. Run a scan to re-check the site.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {issues.map((it, i) => {
              const s = sevOf(pick(it, ['severity', 'level']))
              const Icon = s.icon
              const title = pick(it, ['title', 'issue', 'name', 'label', 'message'])
              const detail = pick(it, ['detail', 'description', 'recommendation', 'note'])
              const count = pick(it, ['count', 'pages', 'affected'])
              return (
                <div key={i} className="seo-issue seo-fade" style={{ borderLeftColor: s.t.c }}>
                  <span style={{ ...pill(s.t), flex: 'none', width: 30, height: 30, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={15} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="seo-narr" style={{ fontSize: 14 }}>{dash(title)}</p>
                      <span className="seo-badge" style={pill(s.t)}>{s.label}</span>
                      {count != null && isNum(num(count)) && <span className="seo-chip">{fmtInt(count)} affected</span>}
                    </div>
                    {detail != null && typeof detail !== 'object' && <p className="seo-muted" style={{ fontSize: 12.5, marginTop: 4 }}>{String(detail)}</p>}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ---- AI VISIBILITY (hero tab) ---------------------------------------------
function AiVisibilitySection() {
  // The overview endpoint already carries AI-visibility; reuse it so this stays one contract.
  const { data, sample, loading, error } = useSeo('/seo/overview')
  if (loading) return <Loading label="Probing the answer engines…" />
  if (error || !data) return <ErrorCard />

  const aiVis = num(pick(data, ['aiVisibility', 'aiVisibilityScore', 'ai_visibility'], 0), 0)
  const engines = arr(pick(data, ['engines', 'aiEngines', 'ai_engines']))
  const cited = engines.filter((e) => !!pick(e, ['cited', 'isCited', 'present'], false)).length
  const vt = scoreTone(aiVis)

  return (
    <div className="space-y-6 seo-fade">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="seo-sec-h"><Sparkles size={14} style={{ color: 'var(--seo-purple)' }} /> AI visibility</h3>
        <SampleRibbon show={sample} />
      </div>

      <div className="seo-card seo-hero">
        <div className="flex items-center gap-7 flex-wrap">
          <RingGauge value={aiVis} max={100} size={148} stroke={13} color="var(--seo-purple)" sub="/ 100" label="Cited score" />
          <div style={{ flex: 1, minWidth: 260 }}>
            <p className="seo-narr" style={{ fontSize: 18, marginBottom: 6 }}>
              {num(aiVis, 0) === 0
                ? 'Invisible to AI answers — today'
                : num(aiVis, 0) >= 70 ? 'Strongly cited across answer engines' : 'Emerging AI presence'}
            </p>
            <p className="seo-body">
              As buyers move from ten blue links to a single AI answer, being cited by ChatGPT, Google AI Overview, Gemini and Perplexity is the
              new front page. This is the metric classic SEO tools do not measure — and the one that will decide discovery next.
            </p>
            <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 14 }}>
              <span className="seo-pill" style={pill(vt)}><Gauge size={12} /> Score {isNum(num(aiVis)) ? Math.round(num(aiVis)) : '—'}/100</span>
              <span className="seo-pill" style={pill(cited > 0 ? TONE.up : TONE.mut)}><Target size={12} /> Cited by {cited}/{engines.length || 4} engines</span>
            </div>
          </div>
        </div>
      </div>

      <div className="seo-card">
        <p className="seo-elab" style={{ marginBottom: 14 }}><Sparkles size={13} /> Per-engine citation</p>
        {engines.length === 0 ? (
          <p className="seo-dim" style={{ fontSize: 13, margin: 0 }}>No engine data yet — run an SEO scan to probe each answer engine.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {engines.map((e, i) => {
              const name = pick(e, ['name', 'engine', 'label']) || `Engine ${i + 1}`
              const isCited = !!pick(e, ['cited', 'isCited', 'present'], false)
              const detail = pick(e, ['detail', 'note', 'query', 'lastChecked', 'last_checked'])
              const t = isCited ? TONE.up : TONE.down
              const Icon = isCited ? CheckCircle2 : XCircle
              return (
                <div key={i} className="seo-engine seo-fade">
                  <div style={{ minWidth: 0 }}>
                    <p className="seo-narr" style={{ fontSize: 14 }}>{name}</p>
                    {detail != null && typeof detail !== 'object' && <p className="seo-muted" style={{ fontSize: 12, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(detail)}</p>}
                  </div>
                  <span className="seo-badge" style={{ ...pill(t), flex: 'none' }}><Icon size={12} /> {isCited ? 'Cited' : 'Not cited'}</span>
                </div>
              )
            })}
          </div>
        )}
        <p className="seo-dim" style={{ fontSize: 11.5, marginTop: 16, display: 'flex', alignItems: 'center', gap: 5 }}>
          <Info size={12} /> Citation is probed with representative prompts for your space. Improve it by publishing citable, well-structured answer content.
        </p>
      </div>
    </div>
  )
}
